import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { API_URL } from '../utils/config';

const STATES = { LOADING: 'loading', SUCCESS: 'success', FAILED: 'failed' };

export default function VerifyEmail() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [status, setStatus] = useState(STATES.LOADING);
  const [message, setMessage] = useState('');
  const calledRef = useRef(false);

  const isDark = theme === 'dark';

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    async function verify() {
      try {
        const res = await fetch(`${API_URL}/auth/verify_email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });
        const data = await res.json().catch(() => ({}));

        if (res.ok) {
          setStatus(STATES.SUCCESS);
          setMessage(data.message || 'Email verified. Thanks!');
        } else {
          setStatus(STATES.FAILED);
          setMessage(data.error || 'We could not verify your email. The link may have expired.');
        }
      } catch {
        setStatus(STATES.FAILED);
        setMessage('We could not reach the server. Please check your connection and try again.');
      }
    }
    verify();
  }, [token]);

  const wrapClass = `min-h-screen flex items-center justify-center p-6 ${
    isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
  }`;
  const cardClass = `max-w-md w-full rounded-xl p-8 text-center ${
    isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'
  }`;

  return (
    <div className={wrapClass}>
      <div className={cardClass}>
        {status === STATES.LOADING && (
          <>
            <Loader2 className="w-12 h-12 mx-auto mb-4 text-blue-500 animate-spin" />
            <h1 className="text-xl font-semibold mb-2">Verifying your email…</h1>
            <p className="text-sm text-gray-500">This only takes a moment.</p>
          </>
        )}

        {status === STATES.SUCCESS && (
          <>
            <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
            <h1 className="text-xl font-semibold mb-2">Email verified</h1>
            <p className="text-sm mb-6">{message}</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              Continue to JPro
            </button>
          </>
        )}

        {status === STATES.FAILED && (
          <>
            <XCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
            <h1 className="text-xl font-semibold mb-2">Verification failed</h1>
            <p className="text-sm mb-6">{message}</p>
            <div className="space-y-2">
              <Link
                to="/login"
                className="block px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                Go to sign in
              </Link>
              <p className="text-xs text-gray-500">
                Already signed in? You can request a new verification email from the banner on your dashboard.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
